import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { GripVertical, ChevronUp, ChevronDown, Eye, EyeOff, ArrowUpDown } from "lucide-react";
import { api } from "@/lib/api";

// Widget order & visibility & size persistence — backend-first, localStorage fallback.
// iter326 — Scope parameter: "admin" (default) → /me/dashboard-prefs (per-user);
// "user_control" → /user-control-dashboard-prefs (SHARED across all users;
// write-gated to admin + system_authority). The hook returns `canEdit` so
// UI can hide edit toggles for unauthorized viewers.
const SCOPES = {
  admin: {
    get: "/me/dashboard-prefs",
    put: "/me/dashboard-prefs",
    ls: "erp_dashboard_widget_order",
    canEditResolver: () => true,  // own prefs — anyone can edit their own view
  },
  user_control: {
    get: "/user-control-dashboard-prefs",
    put: "/user-control-dashboard-prefs",
    ls: "erp_user_control_dashboard_widget_order",
    canEditResolver: (payload) => Boolean(payload?.can_edit),
  },
  // iter329.8 — Vergi Hesabı sayfa düzeni (shared; admin/system_authority gated)
  tax_calculation: {
    get: "/tax-calculation-page-prefs",
    put: "/tax-calculation-page-prefs",
    ls: "erp_tax_calculation_page_order",
    canEditResolver: (payload) => Boolean(payload?.can_edit),
  },
};

const STORAGE_KEY = "erp_dashboard_widget_order";
const STORAGE_SIZES = "erp_dashboard_widget_sizes";

// Size passed via React context (not prop injection) so DOM elements like
// <Card>/<div> don't receive an unknown `widgetSize` prop → prevents React
// "unknown prop on DOM element" warnings.
const WidgetSizeContext = React.createContext("FULL");
export const useWidgetSize = () => useContext(WidgetSizeContext);

export const WIDGET_SIZES = ["S", "M", "L", "FULL"];
export const SIZE_LABELS = { S: "Küçük", M: "Orta", L: "Büyük", FULL: "Tam" };
// Tailwind col-span classes (parent grid = grid-cols-4 on lg).
export const SIZE_COL_CLASS = {
  S: "col-span-4 md:col-span-2 lg:col-span-1",
  M: "col-span-4 md:col-span-2 lg:col-span-2",
  L: "col-span-4 md:col-span-4 lg:col-span-3",
  FULL: "col-span-4",
};

export function useWidgetOrder(defaultOrder, defaultSizes = {}, scope = "admin") {
  const scopeCfg = SCOPES[scope] || SCOPES.admin;
  const lsKey = scopeCfg.ls;
  const lsSizesKey = `${lsKey}_sizes`;
  const [order, setOrder] = useState(defaultOrder);
  const [hidden, setHidden] = useState(new Set());
  const [sizes, setSizes] = useState(defaultSizes);
  const [editMode, setEditMode] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [canEdit, setCanEdit] = useState(scope === "admin");
  const skipSaveRef = useRef(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(scopeCfg.get);
        if (!mounted) return;
        setCanEdit(scopeCfg.canEditResolver(r));
        const storedOrder = Array.isArray(r?.order) ? r.order : [];
        const storedHidden = Array.isArray(r?.hidden) ? r.hidden : [];
        const storedSizes = (r?.sizes && typeof r.sizes === "object") ? r.sizes : {};
        if (storedOrder.length > 0) {
          const merged = [...storedOrder.filter(k => defaultOrder.includes(k))];
          for (const k of defaultOrder) if (!merged.includes(k)) merged.push(k);
          setOrder(merged);
        }
        setHidden(new Set(storedHidden.filter(k => defaultOrder.includes(k))));
        // Merge: stored overrides > defaults
        setSizes({ ...defaultSizes, ...storedSizes });
      } catch {
        try {
          const stored = JSON.parse(localStorage.getItem(lsKey) || "null");
          if (Array.isArray(stored) && stored.length > 0) {
            const merged = [...stored.filter(k => defaultOrder.includes(k))];
            for (const k of defaultOrder) if (!merged.includes(k)) merged.push(k);
            setOrder(merged);
          }
          const h = JSON.parse(localStorage.getItem(lsKey + "_hidden") || "[]");
          if (Array.isArray(h)) setHidden(new Set(h));
          const sz = JSON.parse(localStorage.getItem(lsSizesKey) || "null");
          if (sz && typeof sz === "object") setSizes({ ...defaultSizes, ...sz });
        } catch {}
      } finally {
        if (mounted) setLoaded(true);
        setTimeout(() => { skipSaveRef.current = false; }, 100);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [scope]);

  useEffect(() => {
    if (skipSaveRef.current) return;
    // Local cache is user-scoped (OK to persist even without edit rights —
    // it's just a hint; server is the authority).
    localStorage.setItem(lsKey, JSON.stringify(order));
    localStorage.setItem(lsKey + "_hidden", JSON.stringify(Array.from(hidden)));
    localStorage.setItem(lsSizesKey, JSON.stringify(sizes));
    // Only attempt remote save if the current user has edit rights for this scope.
    if (canEdit) {
      api.put(scopeCfg.put, {
        order, hidden: Array.from(hidden), sizes,
      }).catch(() => {});
    }
  }, [order, hidden, sizes]);

  const move = useCallback((key, dir) => {
    setOrder(prev => {
      const idx = prev.indexOf(key);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  }, []);
  const reorderTo = useCallback((srcKey, dstKey) => {
    if (!srcKey || !dstKey || srcKey === dstKey) return;
    setOrder(prev => {
      const arr = [...prev];
      const from = arr.indexOf(srcKey);
      const to = arr.indexOf(dstKey);
      if (from < 0 || to < 0) return prev;
      arr.splice(from, 1);
      const insertAt = from < to ? to - 1 : to;
      arr.splice(insertAt, 0, srcKey);
      return arr;
    });
  }, []);
  const toggleHidden = useCallback((key) => {
    setHidden(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  }, []);
  const setSize = useCallback((key, size) => {
    if (!WIDGET_SIZES.includes(size)) return;
    setSizes(prev => ({ ...prev, [key]: size }));
  }, []);
  const cycleSize = useCallback((key) => {
    setSizes(prev => {
      const cur = prev[key] || defaultSizes[key] || "L";
      const idx = WIDGET_SIZES.indexOf(cur);
      const next = WIDGET_SIZES[(idx + 1) % WIDGET_SIZES.length];
      return { ...prev, [key]: next };
    });
  }, [defaultSizes]);
  const resetOrder = () => {
    setOrder(defaultOrder);
    setHidden(new Set());
    setSizes(defaultSizes);
  };

  return {
    order, hidden, sizes, editMode, setEditMode,
    move, reorderTo, toggleHidden, setSize, cycleSize, resetOrder, loaded,
    canEdit,
    // iter329.8 — Expose raw setters so hosts can implement
    // snapshot/revert semantics (needed for "İptal" button).
    setOrder, setHidden, setSizes,
  };
}

export function WidgetWrapper({
  id, title, editMode, isFirst, isLast, isHidden, size,
  onMove, onToggleHide, onCycleSize, onDragStart, onDragOver, onDrop,
  children,
}) {
  if (isHidden && !editMode) return null;
  const currentSize = size || "FULL";
  const colClass = SIZE_COL_CLASS[currentSize] || SIZE_COL_CLASS.FULL;
  // Size is delivered to children via context (see useWidgetSize) so we no
  // longer inject `widgetSize` as a prop — this avoids React warnings when a
  // child ultimately spreads props onto a DOM element (e.g. <Card> → <div>).
  return (
    <div
      className={`${colClass} relative min-w-0 ${editMode ? "ring-2 ring-indigo-300 ring-offset-2 rounded-lg" : ""}`}
      data-testid={`widget-${id}`}
      data-widget-size={currentSize}
      draggable={!!editMode}
      onDragStart={editMode ? onDragStart : undefined}
      onDragOver={editMode ? onDragOver : undefined}
      onDrop={editMode ? onDrop : undefined}
    >
      {editMode && (
        <div className="absolute -top-3 left-4 z-10 bg-white dark:bg-slate-800 border border-indigo-300 dark:border-indigo-500/50 rounded-full px-2 py-0.5 flex items-center gap-1 text-xs shadow">
          <GripVertical size={11} className="text-indigo-400"/>
          <span className="font-semibold text-indigo-700 dark:text-indigo-400">{title}</span>
          <button onClick={() => onMove(id, -1)} disabled={isFirst} className="p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-30" data-testid={`widget-up-${id}`}><ChevronUp size={12}/></button>
          <button onClick={() => onMove(id, 1)} disabled={isLast} className="p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-30" data-testid={`widget-down-${id}`}><ChevronDown size={12}/></button>
          {onCycleSize && (
            <button onClick={() => onCycleSize(id)} className="px-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/40 flex items-center gap-1 font-mono text-[10px] text-indigo-700 dark:text-indigo-300" data-testid={`widget-size-${id}`} title={`Boyut: ${SIZE_LABELS[currentSize]}`}>
              <ArrowUpDown size={10}/>{currentSize}
            </button>
          )}
          <button onClick={() => onToggleHide(id)} className="p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/40" data-testid={`widget-hide-${id}`}>{isHidden ? <EyeOff size={12} className="text-slate-400"/> : <Eye size={12}/>}</button>
        </div>
      )}
      <div className={`${isHidden ? "opacity-30" : ""} overflow-hidden`}>
        <WidgetSizeContext.Provider value={currentSize}>{children}</WidgetSizeContext.Provider>
      </div>
    </div>
  );
}

export function WidgetEditButton({ editMode, setEditMode, resetOrder }) {
  return (
    <div className="flex gap-2 items-center">
      {editMode && (
        <button onClick={resetOrder} className="text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-2.5 py-1 rounded border border-slate-300 dark:border-slate-600" data-testid="widget-reset">Varsayılana Dön</button>
      )}
      <button onClick={() => setEditMode(!editMode)} data-testid="widget-edit-toggle"
        className={`text-xs px-2.5 py-1 rounded border font-semibold transition ${editMode ? "bg-indigo-600 text-white border-indigo-700" : "bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-400 border-indigo-300 dark:border-indigo-500/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"}`}>
        {editMode ? "✓ Düzenlemeyi Bitir" : "⇅ Widget Sıralama"}
      </button>
    </div>
  );
}
